// src/features/auth/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { loginUser, clearAuthStatus } from './authSlice';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // ADDED: Import useTranslation
import { Typography, Box, TextField, Button, CircularProgress, Alert, Divider, Link } from '@mui/material';
import AuthLayout from '../../layout/AuthLayout';
import PasswordField from '../../components/common/PasswordField';
import GoogleAuthButton from '../../components/auth/GoogleAuthButton';

const LoginPage: React.FC = () => {
    const { t } = useTranslation(); // ADDED: Initialize translation
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { status, error, isAuthenticated } = useAppSelector((state) => state.auth);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    useEffect(() => {
        // Clear previous errors/messages on component mount
        dispatch(clearAuthStatus());
    }, [dispatch]);

    useEffect(() => {
        // Redirect to homepage if already authenticated
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (email && password) {
            dispatch(loginUser({ email, password }));
        }
    };

    const isLoading = status.auth === 'loading';

    return (
        <AuthLayout>
            <Typography component="h1" variant="h5">{t('login')}</Typography>
            {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                <TextField
                    margin="normal" required fullWidth id="email" label={t('email_address')} name="email"
                    value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoFocus
                />
                <PasswordField
                    name="password"
                    label={t('password')}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                />
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                        mt: 3,
                        mb: 2,
                        // UPDATED: Use inverse theme colors
                        bgcolor: 'inverse.main',
                        color: 'inverse.contrastText',
                        '&:hover': {
                            bgcolor: 'inverse.hover.main',
                        },
                    }}
                    disabled={isLoading}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('login')}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Link component={RouterLink} to="/forgot-password" variant="body2" color="inherit">
                        {t('forgot_password')}
                    </Link>
                </Box>

                <Divider sx={{ my: 2, width: '100%' }}>{t('or')}</Divider>

                <GoogleAuthButton />

                <Box textAlign="center" sx={{ mt: 1 }}>
                    <Link component={RouterLink} to="/signup" color="inherit">
                        {t('no_account_sign_up')}
                    </Link>
                </Box>
            </Box>
        </AuthLayout>
    );
};

export default LoginPage;